import { useMemo } from "react";
import type { ThreeEvent } from "@react-three/fiber";

import { COLORS } from "./dioramaLayout";

interface SandPlatformProps {
  onClick?: (event: ThreeEvent<MouseEvent>) => void;
  onPointerDown?: (event: ThreeEvent<PointerEvent>) => void;
}

function pseudoRandom(seed: number): number {
  return ((Math.sin(seed) * 43758.5453) % 1 + 1) % 1;
}

export function SandPlatform({
  onClick,
  onPointerDown,
}: SandPlatformProps) {
  const bumps = useMemo(() => {
    const result: Array<{
      key: number;
      position: [number, number, number];
      radius: number;
    }> = [];
    for (let i = 0; i < 30; i++) {
      const angle = pseudoRandom(i * 2.399) * Math.PI * 2;
      const dist = pseudoRandom(i * 12345.6789) * 6.5;
      const radius = 0.08 + pseudoRandom(i * 9876.543) * 0.12;
      result.push({
        key: i,
        position: [Math.cos(angle) * dist, 0.02, Math.sin(angle) * dist],
        radius,
      });
    }
    return result;
  }, []);

  return (
    <group name="sand-platform">
      {/* Main platform body — keeps "ocean-floor" name for RTS raycasting */}
      <mesh
        position={[0, -0.6, 0]}
        receiveShadow
        name="ocean-floor"
        onClick={onClick}
        onPointerDown={onPointerDown}
      >
        <cylinderGeometry args={[7.5, 8, 1.2, 8]} />
        <meshStandardMaterial color={COLORS.sand} flatShading />
      </mesh>

      {/* Platform rim */}
      <mesh position={[0, -1.1, 0]} receiveShadow>
        <cylinderGeometry args={[8.1, 8.3, 0.3, 8]} />
        <meshStandardMaterial color={COLORS.sandDark} flatShading />
      </mesh>

      {/* Sand texture bumps */}
      {bumps.map((bump) => (
        <mesh
          key={bump.key}
          position={bump.position}
          scale={[1, 0.3, 1]}
          receiveShadow
        >
          <sphereGeometry args={[bump.radius, 4, 3]} />
          <meshStandardMaterial color={COLORS.sandDark} flatShading />
        </mesh>
      ))}
    </group>
  );
}
